// src/pages/BidDetails/PriceQuotesBasic/EditSubvendorFormikModal.jsx
import React from 'react';
import { Modal, Divider, Row, Col, message } from 'antd';
import { Formik, FieldArray } from 'formik';
import { Form, InputNumber } from 'formik-antd';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import FormControl from '~/components/Form/FormControl';
import FormTxt from '~/components/Form/FormTxt';
import { ModalFooter } from '~/components/Modal';
import { formatPrice } from '~/Utils';

export default function EditSubvendorFormikModal({ visible, record, onCancel, onSave, loadingParent }) {
  const { t } = useTranslation();

  if (!record || !Array.isArray(record.items) || record.items.length === 0) {
    return null;
  }

  // Assumimos que `record.items` já está filtrado pelo laborType correto
  const initialValues = {
    items: record.items.map(item => ({
      id: item.id,
      description: item.description,
      unit: item.unit,
      quantity: item.quantity || 0,
      baseMaterial: item.ownMaterialValue || 0,
      baseLabor: item.ownLaborValue || 0,
      materialValue: item.materialValue != null ? item.materialValue : 0,
      laborValue: item.laborValue != null ? item.laborValue : 0,
    })),
  };

  const validationSchema = Yup.object().shape({
    items: Yup.array().of(
      Yup.object().shape({
        materialValue: Yup.number()
          .min(0, t('screens:estimate.validation.minZero'))
          .required(t('screens:estimate.validation.required')),
        laborValue: Yup.number()
          .min(0, t('screens:estimate.validation.minZero'))
          .required(t('screens:estimate.validation.required')),
      })
    ),
  });

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        const updatedItems = values.items.map(item => ({
          id: item.id,
          materialValue: parseFloat(item.materialValue),
          laborValue: parseFloat(item.laborValue),
        }));

        try {
          await onSave(updatedItems);
          message.success(t('screens:estimate.subvendorValuesSaved'));
        } catch (err) {
          console.error(err);
          message.error(t('screens:estimate.errorSavingSubvendor'));
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, handleSubmit, isSubmitting }) => (
        <Modal
          width={600}
          title={t('screens:estimate.editSubcontractorValues')}
          visible={visible}
          onCancel={onCancel}
          footer={
            <ModalFooter
              onOk={handleSubmit}
              loading={isSubmitting || loadingParent}
              onCancel={onCancel}
              cancelColor="default"
            />
          }
        >
          <Form layout="vertical">
            <FieldArray
              name="items"
              render={() =>
                values.items.map((item, index) => {
                  const matVal = Number(item.materialValue || 0);
                  const labVal = Number(item.laborValue || 0);
                  const qty = Number(item.quantity || 0);

                  const totalUnit = matVal + labVal;
                  const totalMaterial = matVal * qty;
                  const totalLabor = labVal * qty;
                  const totalCost = totalMaterial + totalLabor;

                  return (
                    <div
                      key={item.id}
                      //   style={{
                      //     marginBottom: 24,
                      //     padding: '12px 0',
                      //     borderBottom: '1px solid #eee',
                      //   }}
                    >
                      <Row>
                        <Col span={12}>
                          <FormTxt label={t('screens:estimate.data.description')}>{item.description}</FormTxt>
                        </Col>
                        <Col span={6}>
                          <FormTxt label={t('screens:estimate.data.quantity')}>{item.quantity}</FormTxt>
                        </Col>
                        <Col span={6}>
                          <FormTxt label={t('screens:estimate.data.unit')}>{item.unit}</FormTxt>
                        </Col>
                      </Row>

                      <Divider style={{ margin: '16px 0' }} />

                      <Row gutter={[16, 16]}>
                        <Col span={8}>
                          {/* MATERIAL UNIT PRICE */}
                          <FormControl
                            field={`items[${index}].materialValue`}
                            label={t('screens:estimate.data.materialValue')}
                            required
                            error={
                              touched.items &&
                              touched.items[index] &&
                              errors.items &&
                              errors.items[index] &&
                              errors.items[index].materialValue
                            }
                          >
                            <InputNumber
                              name={`items[${index}].materialValue`}
                              addonBefore="$"
                              placeholder="Enter material unit cost"
                              style={{ width: '100%' }}
                            />
                          </FormControl>
                        </Col>

                        <Col span={8}>
                          {/* LABOR UNIT PRICE */}
                          <FormControl
                            field={`items[${index}].laborValue`}
                            label={t('screens:estimate.data.laborValue')}
                            required
                            error={
                              touched.items &&
                              touched.items[index] &&
                              errors.items &&
                              errors.items[index] &&
                              errors.items[index].laborValue
                            }
                          >
                            <InputNumber
                              name={`items[${index}].laborValue`}
                              addonBefore="$"
                              placeholder="Enter labor unit cost"
                              style={{ width: '100%' }}
                            />
                          </FormControl>
                        </Col>

                        <Col span={8}>
                          {/* TOTAL UNIT */}
                          <FormTxt label={t('screens:estimate.data.totalUnit')}>{formatPrice(totalUnit)}</FormTxt>
                        </Col>
                      </Row>
                      <Row gutter={[16, 16]}>
                        <Col span={8}>
                          {/* TOTAL MATERIAL PRICE */}
                          <FormTxt label={t('screens:estimate.data.totalMaterial')}>
                            {formatPrice(totalMaterial)}
                          </FormTxt>
                        </Col>

                        <Col span={8}>
                          {/* TOTAL LABOR PRICE */}
                          <FormTxt label={t('screens:estimate.data.totalLabor')}>{formatPrice(totalLabor)}</FormTxt>
                        </Col>

                        <Col span={8}>
                          {/* TOTAL PRICE */}
                          <FormTxt label={t('screens:estimate.data.totalMaterialAndLabor')}>
                            {formatPrice(totalCost)}
                          </FormTxt>
                        </Col>
                      </Row>
                    </div>
                  );
                })
              }
            />
          </Form>
        </Modal>
      )}
    </Formik>
  );
}
